import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Sparkles, ArrowLeft, Shield, Eye, Lock, Database, Mail, Cookie, Cloud, Trash2, Image } from 'lucide-react';

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const homeRoute = isAuthenticated ? '/dashboard' : '/';
  const lastUpdated = "March 17, 2026";
  const contactEmail = "privacy@virtualfit.work";

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-rose-50 to-orange-50">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-md shadow-sm sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <button
            onClick={() => navigate(homeRoute)}
            className="flex items-center space-x-2 text-gray-700 hover:text-rose-500 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-semibold">Back to Home</span>
          </button>
          
          <button
            onClick={() => navigate(homeRoute)}
            className="flex items-center space-x-2 cursor-pointer hover:opacity-80 transition-opacity"
          >
            <Sparkles className="w-7 h-7 text-rose-400" />
            <span className="text-xl font-bold bg-gradient-to-r from-rose-400 to-orange-400 bg-clip-text text-transparent">
              VirtualFit
            </span>
          </button>
          
          <div className="w-24"></div>
        </div>
      </nav>

      <div className="container mx-auto px-6 py-12 max-w-4xl">
        <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-rose-400 to-orange-400 rounded-full mb-6">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-gray-800 mb-4">Privacy Policy</h1>
            <p className="text-gray-600">Last updated: {lastUpdated}</p>
            <div className="mt-3 p-4 bg-gray-50 rounded-xl text-left">
              <p className="text-gray-700 text-sm">
                <strong>Data Controller:</strong> VirtualFit (a product of emergent.sh)<br />
                <strong>Contact:</strong>{' '}
                <a href={`mailto:${contactEmail}`} className="text-rose-500 hover:underline">{contactEmail}</a>
              </p>
            </div>
          </div>

          {/* Content */}
          <div className="prose prose-lg max-w-none">
            
            {/* Introduction */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                <Eye className="w-6 h-6 mr-3 text-rose-500" />
                Introduction
              </h2>
              <p className="text-gray-600 leading-relaxed">
                Welcome to VirtualFit ("we," "our," or "us"). We are committed to protecting your privacy 
                and ensuring the security of your personal information. This Privacy Policy explains how 
                we collect, use, disclose, and safeguard your information when you use our virtual try-on 
                platform and services.
              </p>
              <p className="text-gray-600 leading-relaxed mt-4">
                By using VirtualFit, you agree to the collection and use of information in accordance 
                with this policy. If you do not agree with this policy, please do not use our services.
              </p>
            </section>

            {/* Information We Collect */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                <Database className="w-6 h-6 mr-3 text-rose-500" />
                Information We Collect
              </h2>
              
              <h3 className="text-xl font-semibold text-gray-700 mt-6 mb-3">Personal Information</h3>
              <ul className="list-disc list-inside text-gray-600 space-y-2">
                <li>Account information (name, email, password)</li>
                <li>Profile information (handle, bio, profile image)</li>
                <li>Photos you upload for virtual try-on purposes</li>
                <li>Clothing images you upload to your portfolio</li>
                <li>Size and fit preferences you select</li>
                <li>Payment information (processed securely via Stripe)</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-700 mt-6 mb-3">Automatically Collected Information</h3>
              <ul className="list-disc list-inside text-gray-600 space-y-2">
                <li>Device information (browser type, operating system)</li>
                <li>IP address and general location</li>
                <li>Browser fingerprint (for free trial tracking)</li>
                <li>Usage data (pages visited, features used)</li>
                <li>Cookies and similar tracking technologies</li>
              </ul>
            </section>

            {/* Image Storage */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                <Cloud className="w-6 h-6 mr-3 text-rose-500" />
                Image Storage & Third-Party Services
              </h2>
              <p className="text-gray-600 leading-relaxed">
                Your images are stored using Cloudinary, a secure cloud-based image management service. This includes:
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2 mt-4">
                <li>Profile pictures</li>
                <li>Portfolio images (for Brands and Influencers)</li>
                <li>AI-generated virtual try-on images</li>
                <li>Collaboration content</li>
              </ul>
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mt-4">
                <p className="text-blue-800 text-sm">
                  <strong>Note:</strong> AI-generated images are automatically watermarked with "VirtualFit" 
                  to protect intellectual property and indicate AI-generated content.
                </p>
              </div>
            </section>

            {/* How We Use Your Information */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                <Lock className="w-6 h-6 mr-3 text-rose-500" />
                How We Use Your Information
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                We use the information we collect for the following purposes:
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2">
                <li>To provide and maintain our virtual try-on service</li>
                <li>To generate AI-powered outfit visualizations</li>
                <li>To manage your account and subscriptions</li>
                <li>To facilitate brand-influencer collaborations and campaigns</li>
                <li>To process payments and campaign transactions</li>
                <li>To save your portfolio and try-on results</li>
                <li>To improve and optimize our platform</li>
                <li>To communicate with you about updates, campaigns, or support</li>
                <li>To detect and prevent fraud or abuse</li>
              </ul>
            </section>

            {/* Data Retention & Deletion */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                <Trash2 className="w-6 h-6 mr-3 text-rose-500" />
                Data Retention & Your Right to Deletion
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                We retain your data only as long as necessary for the purposes described in this policy. 
                Below is our specific data retention schedule:
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-gray-600 border border-gray-200 rounded-xl overflow-hidden">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left px-4 py-3 font-semibold text-gray-700 border-b">Data Type</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-700 border-b">Retention Period</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b"><td className="px-4 py-3">Account data</td><td className="px-4 py-3">Retained while account is active. After subscription cancellation, retained for a 30-day cold-storage window before permanent deletion.</td></tr>
                    <tr className="border-b bg-gray-50"><td className="px-4 py-3">Uploaded photos (free trial)</td><td className="px-4 py-3">Deleted within 24 hours</td></tr>
                    <tr className="border-b"><td className="px-4 py-3">Portfolio images, try-on history, collaboration content</td><td className="px-4 py-3">Retained during subscription and for 30 days after cancellation. Fully restored on resubscribe within this window.</td></tr>
                    <tr className="border-b bg-gray-50"><td className="px-4 py-3">Biometric data</td><td className="px-4 py-3">See <a href="/biometric-policy" className="text-rose-500 hover:underline">Biometric Data Policy</a></td></tr>
                    <tr className="border-b"><td className="px-4 py-3">Payment records</td><td className="px-4 py-3">7 years (tax/legal requirement)</td></tr>
                    <tr className="border-b bg-gray-50"><td className="px-4 py-3">Analytics data</td><td className="px-4 py-3">12 months rolling</td></tr>
                    <tr><td className="px-4 py-3">Server logs</td><td className="px-4 py-3">90 days</td></tr>
                  </tbody>
                </table>
              </div>
              <h3 className="text-xl font-semibold text-gray-700 mt-6 mb-3">Account Deletion</h3>
              <p className="text-gray-600 leading-relaxed">
                When your subscription is cancelled and your billing period ends, your account enters a <strong>30-day cold-storage window</strong>. During this window your data is preserved but the account is locked from feature access. If you resubscribe to any plan within these 30 days, your portfolio, try-on history, collaborations, chat messages and analytics are fully restored.
              </p>
              <p className="text-gray-600 leading-relaxed mt-3">
                After the 30-day window expires, or when your account is deleted (by you, an administrator, or on GDPR/CCPA request), we permanently delete:
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2 mt-4">
                <li>Your profile and account information</li>
                <li>All uploaded images from our cloud storage (Cloudinary)</li>
                <li>Portfolio items and collaboration content</li>
                <li>Messages and campaign history</li>
                <li>Analytics and usage data associated with your account</li>
              </ul>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mt-4">
                <p className="text-amber-800 text-sm">
                  <strong>GDPR/CCPA Compliance:</strong> To request deletion of your data, contact us at {contactEmail} 
                  or contact our support team. We will process your request within 30 days.
                </p>
              </div>
            </section>

            {/* AI-Generated Content */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                <Image className="w-6 h-6 mr-3 text-rose-500" />
                AI-Generated Content
              </h2>
              <p className="text-gray-600 leading-relaxed">
                Our platform uses AI technology (Google Gemini) to generate virtual try-on images. Please note:
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2 mt-4">
                <li>AI-generated images are watermarked for identification</li>
                <li>Generated images are stored on Cloudinary for your access</li>
                <li>We do not use your photos to train AI models</li>
                <li>Your uploaded photos are shared only with our contracted data processors (Google LLC for AI processing and Cloudinary Ltd. for image storage) under Data Processing Agreements, solely to provide the virtual try-on service. They are not shared with any other third parties.</li>
              </ul>
            </section>

            {/* Legal Basis for Processing (GDPR Article 6) */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                <Shield className="w-6 h-6 mr-3 text-rose-500" />
                Legal Basis for Processing (GDPR Article 6)
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                For users in the EU/EEA, we rely on the following legal bases to process your personal data:
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-3">
                <li><strong>Performance of Contract (Article 6(1)(b)):</strong> Account management, subscription services, payment processing, campaign facilitation, and delivering the virtual try-on service you requested.</li>
                <li><strong>Consent (Article 6(1)(a)):</strong> Biometric data processing (facial geometry, body proportions), non-essential cookies and tracking (analytics, browser fingerprinting). You may withdraw consent at any time without affecting the lawfulness of prior processing.</li>
                <li><strong>Legitimate Interest (Article 6(1)(f)):</strong> Fraud prevention and abuse detection, platform security and infrastructure monitoring, and general service improvement based on aggregated, anonymized usage data.</li>
                <li><strong>Legal Obligation (Article 6(1)(c)):</strong> Retention of tax records and payment information as required by law, responding to valid legal requests, and regulatory compliance.</li>
              </ul>
            </section>

            {/* Data Security */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                <Shield className="w-6 h-6 mr-3 text-rose-500" />
                Data Security
              </h2>
              <p className="text-gray-600 leading-relaxed">
                We implement appropriate technical and organizational measures to protect your personal information:
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2 mt-4">
                <li>Encrypted data transmission (HTTPS/TLS)</li>
                <li>Secure password hashing (bcrypt)</li>
                <li>Two-factor authentication for admin accounts</li>
                <li>API rate limiting to prevent abuse</li>
                <li>Regular security audits and monitoring</li>
              </ul>
            </section>

            {/* Cookies */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                <Cookie className="w-6 h-6 mr-3 text-rose-500" />
                Cookies and Tracking Technologies
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                We use cookies and similar technologies on our platform. When you first visit VirtualFit, 
                you will be shown a cookie consent banner that allows you to accept or reject non-essential cookies.
              </p>

              <h3 className="text-xl font-semibold text-gray-700 mt-6 mb-3">Essential Cookies (Always Active)</h3>
              <p className="text-gray-600 leading-relaxed mb-2">
                These cookies are strictly necessary for the website to function. They cannot be switched off. They include:
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2">
                <li><strong>Authentication tokens (JWT)</strong> — Keeps you logged in during your session</li>
                <li><strong>Cookie consent preference</strong> — Remembers your cookie choice so we don't ask again</li>
                <li><strong>Session data</strong> — Maintains your active state while using the platform</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-700 mt-6 mb-3">Tracking / Non-Essential Cookies (Require Consent)</h3>
              <p className="text-gray-600 leading-relaxed mb-2">
                These cookies are only set if you click "Accept All" or enable them via "Customize" on the cookie banner:
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2">
                <li><strong>PostHog Analytics (_ph_* cookies)</strong> — Used to understand how visitors use the site so we can improve it. Collects anonymized page views, feature usage, and session replays. Data is aggregated and not linked to your personal identity.</li>
                <li><strong>Browser fingerprint (FingerprintJS)</strong> — Used to track free trial usage and prevent abuse. If you reject this, we fall back to IP-based tracking which is less accurate.</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-700 mt-6 mb-3">Managing Your Preferences</h3>
              <p className="text-gray-600 leading-relaxed">
                You can change your cookie preferences at any time by clearing your browser's local storage for this site, 
                which will cause the consent banner to reappear on your next visit. You can also configure your browser 
                to block cookies, though this may impact essential functionality like logging in.
              </p>

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mt-4">
                <p className="text-blue-800 text-sm">
                  <strong>GDPR & ePrivacy Compliance:</strong> Non-essential cookies and tracking technologies 
                  are only activated after you provide explicit consent. If you reject non-essential cookies, 
                  the free trial feature will still work using IP-based tracking as a fallback.
                </p>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mt-4">
                <p className="text-amber-800 text-sm">
                  <strong>CCPA Notice (California Residents):</strong> Under the California Consumer Privacy Act, 
                  you have the right to opt out of the "sale" of personal information. We do not sell your personal data. 
                  Browser fingerprints are used solely for free trial management and fraud prevention. You can opt out 
                  of non-essential tracking by selecting "Essential Only" on the cookie consent banner.
                </p>
              </div>
            </section>

            {/* Third-Party Services */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                <Database className="w-6 h-6 mr-3 text-rose-500" />
                Third-Party Services
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                We use the following third-party services to provide our platform:
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2">
                <li><strong>Cloudinary:</strong> Image storage and delivery</li>
                <li><strong>Stripe:</strong> Payment processing</li>
                <li><strong>Google Gemini:</strong> AI image generation</li>
                <li><strong>Resend:</strong> Transactional emails</li>
                <li><strong>FingerprintJS:</strong> Browser identification for free trials</li>
              </ul>
              <p className="text-gray-600 leading-relaxed mt-4">
                Each service has its own privacy policy governing the use of your information. We have executed 
                Data Processing Agreements (DPAs) with Google LLC, Cloudinary Ltd., and Stripe Inc. in accordance 
                with GDPR Article 28.
              </p>
            </section>

            {/* International Data Transfers */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                <Database className="w-6 h-6 mr-3 text-rose-500" />
                International Data Transfers
              </h2>
              <p className="text-gray-600 leading-relaxed">
                Our service providers (Google LLC, Cloudinary Ltd., Stripe Inc., Resend Inc., and FingerprintJS Inc.) 
                are based in the United States. If you are located in the EU/EEA, your personal data will be 
                transferred to and processed in the United States.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mt-4">
                <p className="text-blue-800 text-sm">
                  <strong>GDPR Safeguards:</strong> Transfers of EU/EEA personal data to these providers are 
                  governed by Standard Contractual Clauses (SCCs) approved by the European Commission under 
                  GDPR Article 46(2)(c), or the EU-U.S. Data Privacy Framework where applicable. These 
                  mechanisms ensure that your data receives an adequate level of protection regardless of 
                  where it is processed.
                </p>
              </div>
            </section>

            {/* EU/EEA User Rights */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                <Shield className="w-6 h-6 mr-3 text-rose-500" />
                Your Rights (EU/EEA Users — GDPR)
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                If you are located in the EU/EEA, you have the following rights under the General Data 
                Protection Regulation:
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2">
                <li><strong>Right of Access (Article 15):</strong> Request a copy of all personal data we hold about you.</li>
                <li><strong>Right to Rectification (Article 16):</strong> Request correction of inaccurate or incomplete personal data.</li>
                <li><strong>Right to Erasure (Article 17):</strong> Request deletion of your personal data ("right to be forgotten").</li>
                <li><strong>Right to Restriction (Article 18):</strong> Request that we limit how we process your data.</li>
                <li><strong>Right to Data Portability (Article 20):</strong> Receive your data in a structured, machine-readable format and transfer it to another controller.</li>
                <li><strong>Right to Object (Article 21):</strong> Object to processing based on legitimate interests, including profiling.</li>
                <li><strong>Right Regarding Automated Decision-Making (Article 22):</strong> Not be subject to a decision based solely on automated processing that significantly affects you. VirtualFit does not make automated decisions with legal or similarly significant effects.</li>
                <li><strong>Right to Lodge a Complaint:</strong> File a complaint with your local supervisory authority (Data Protection Authority).</li>
              </ul>
              <p className="text-gray-600 leading-relaxed mt-4">
                To exercise any of these rights, contact us at{' '}
                <a href={`mailto:${contactEmail}`} className="text-rose-500 hover:underline">{contactEmail}</a>. 
                We will respond to verified requests within 30 days. No fee is charged for the first request 
                in any 12-month period.
              </p>
            </section>

            {/* Contact */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                <Mail className="w-6 h-6 mr-3 text-rose-500" />
                Contact Us
              </h2>
              <p className="text-gray-600 leading-relaxed">
                If you have any questions about this Privacy Policy or wish to exercise your data rights, 
                please contact us at:
              </p>
              <div className="bg-gray-50 rounded-xl p-4 mt-4">
                <p className="text-gray-700">
                  <strong>Email:</strong> {contactEmail}<br />
                  <strong>Support:</strong> support@virtualfit.work
                </p>
              </div>
            </section>

          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
